import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatError } from "../lib/api";
import { useDataChange } from "../lib/events";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { Plus, ArrowUpRight, CheckSquare, Calendar, Wallet, IndianRupee, Plane, PlaneTakeoff, PartyPopper, ListChecks, ClipboardList, FolderArchive, Banknote, PhoneCall, UsersRound, PhoneIncoming, PhoneOff } from "lucide-react";

import { fmtDateStr } from "../lib/datetime";

const PRIORITIES = ["Low", "Medium", "High", "Critical"];
function todayStr() { return new Date().toISOString().slice(0, 10); }

export function QuickTaskCard() {
  const [open, setOpen] = useState(false);
  const [people, setPeople] = useState([]);
  const [form, setForm] = useState({ title: "", priority: "Medium", deadline: "", assigned_to_id: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/directory").then(({ data }) => setPeople(data)).catch(() => {});
  }, []);

  const submit = async () => {
    if (!form.title.trim() || !form.deadline || !form.assigned_to_id) {
      toast.error("Title, deadline and assignee required");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/tasks", { ...form, description: "" });
      toast.success("Task assigned");
      setForm({ title: "", priority: "Medium", deadline: "", assigned_to_id: "" });
      setOpen(false);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="pk-card p-5" data-testid="quick-task-card">
      <div className="flex items-center justify-between mb-3">
        <div className="pk-table-header">Quick Task</div>
        <Link to="/tasks" className="text-[11px] text-[var(--pk-accent)] hover:underline flex items-center gap-1">
          See all <ArrowUpRight size={11} />
        </Link>
      </div>
      {!open ? (
        <Button onClick={() => setOpen(true)} variant="outline" className="w-full h-10 border-dashed text-slate-600 hover:text-slate-900" data-testid="quick-task-open">
          <Plus size={14} className="mr-1.5" /> Assign a quick task
        </Button>
      ) : (
        <div className="space-y-2.5">
          <Input
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="Task title…"
            className="h-9 text-sm"
            data-testid="quick-task-title"
          />
          <div className="grid grid-cols-2 gap-2">
            <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
              <SelectTrigger className="h-9 text-sm" data-testid="quick-task-priority"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input
              type="date"
              min={todayStr()}
              value={form.deadline}
              onChange={(e) => setForm({ ...form, deadline: e.target.value })}
              className="h-9 text-sm"
              data-testid="quick-task-deadline"
            />
          </div>
          <Select value={form.assigned_to_id} onValueChange={(v) => setForm({ ...form, assigned_to_id: v })}>
            <SelectTrigger className="h-9 text-sm" data-testid="quick-task-assignee"><SelectValue placeholder="Assign to…" /></SelectTrigger>
            <SelectContent>
              {people.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex gap-2 pt-1">
            <Button onClick={() => setOpen(false)} variant="outline" size="sm" className="h-8 text-xs flex-1">Cancel</Button>
            <Button onClick={submit} disabled={submitting} size="sm" className="h-8 text-xs flex-1 bg-[var(--pk-accent)] hover:bg-[var(--pk-accent-hover)] text-white" data-testid="quick-task-submit">
              {submitting ? "…" : "Assign"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export function RecentTasksCard() {
  const [tasks, setTasks] = useState([]);
  useEffect(() => {
    api.get("/tasks?scope=mine").then(({ data }) => setTasks(data.slice(0, 5))).catch(() => {});
  }, []);
  return (
    <div
      className="relative overflow-hidden rounded-xl border border-violet-200 bg-gradient-to-br from-violet-50 via-white to-fuchsia-50/40 p-5 group hover:-translate-y-0.5 hover:shadow-lg hover:shadow-violet-100 transition-all duration-200"
      data-testid="recent-tasks-card"
    >
      <div className="absolute -top-8 -right-8 w-24 h-24 bg-violet-200/40 rounded-full blur-2xl group-hover:opacity-80 transition-opacity" />
      <div className="relative">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-600 flex items-center justify-center shadow-sm ring-1 ring-violet-300/40">
              <CheckSquare size={16} className="text-white" strokeWidth={2.2} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-violet-700">My Tasks</div>
              <div className="text-[10.5px] text-violet-600/80 font-semibold">{tasks.length} item{tasks.length === 1 ? "" : "s"}</div>
            </div>
          </div>
          <Link to="/tasks" className="inline-flex items-center gap-0.5 text-[10.5px] text-violet-700 hover:underline font-bold">
            See all <ArrowUpRight size={11} />
          </Link>
        </div>
        {tasks.length === 0 ? (
          <div className="text-center py-6 text-slate-400 text-xs italic">No tasks assigned to you.</div>
        ) : (
          <div className="space-y-1.5">
            {tasks.map((t) => (
              <Link
                to="/tasks"
                key={t.id}
                className={`block px-3 py-2 rounded-md text-xs transition-all border bg-white/70 backdrop-blur-sm ${
                  t.overdue ? "border-rose-200 hover:border-rose-300" : "border-violet-100 hover:border-violet-300"
                }`}
                data-testid={`recent-task-${t.id}`}
              >
                <div className="font-semibold text-slate-900 truncate">{t.title}</div>
                <div className="flex items-center gap-2 mt-0.5 text-[10px] text-slate-500 mono">
                  <span>{t.deadline}</span> · <span>{t.status}</span> {t.overdue && <span className="text-rose-600 font-semibold">· Overdue</span>}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function RecentReportsCard() {
  const [reports, setReports] = useState([]);
  useEffect(() => {
    api.get("/records/hr_daily_report").then(({ data }) => setReports(data.slice(0, 5))).catch(() => {});
  }, []);
  return (
    <div className="pk-card p-5" data-testid="recent-reports-card">
      <div className="flex items-center justify-between mb-3">
        <div className="pk-table-header">Recent Daily Reports</div>
        <Link to="/m/hr_daily_report" className="text-[11px] text-[var(--pk-accent)] hover:underline flex items-center gap-1">
          Submit / View <ArrowUpRight size={11} />
        </Link>
      </div>
      {reports.length === 0 ? (
        <div className="text-center py-6 text-slate-400 text-xs">No reports submitted yet.</div>
      ) : (
        <div className="space-y-2">
          {reports.map((r) => (
            <div key={r.id} className="px-3 py-2 border border-slate-200 rounded-md text-xs" data-testid={`recent-report-${r.id}`}>
              <div className="flex items-center justify-between">
                <span className="mono text-slate-700 font-semibold">{fmtDateStr(r.report_date)}</span>
                <span className="mono text-[10px] text-slate-400">{r.report_time || ""}</span>
              </div>
              <div className="text-slate-600 mt-0.5 truncate">{r.tasks_completed || "—"}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function LeaveBalanceCard({ user }) {
  // v109.9.146 — Fetch live balance from /my-leave-balance (applies lazy
  // monthly accrual + reflects debits-on-submit and credits-on-reject).
  // The stale `user.leave_balance` from /auth/me was used as a fallback until
  // the network call completes.
  const [bal, setBal] = useState(user?.leave_balance || { casual: 0, sick: 0, earned: 0 });
  useEffect(() => {
    let mounted = true;
    api.get("/my-leave-balance")
      .then(({ data }) => {
        if (!mounted || !data) return;
        setBal({
          casual: Number(data.casual || 0),
          sick:   Number(data.sick || 0),
          earned: Number(data.earned || 0),
        });
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);
  const total = (bal.casual || 0) + (bal.sick || 0) + (bal.earned || 0);
  const [snap, setSnap] = useState(null);
  const loadSnap = () => api.get("/my-advance-summary").then(({ data }) => setSnap(data)).catch(() => {});
  useEffect(() => { loadSnap(); }, []);
  // v109.9.179 — Re-fetch outstanding-advance + reimbursement stats when
  // Accounts (or anyone) mutates a Travel record.
  useDataChange(["travel:updated", "travel:ledgered", "travel:disbursed"], () => loadSnap());
  const cashInHand = snap?.outstanding ?? 0;
  const reimbPending = snap?.reimbursement_pending ?? 0;
  const reimbCount = snap?.reimbursement_pending_count ?? 0;
  const nh = snap?.next_holiday;
  const fmt = (n) => `₹${Number(n).toLocaleString("en-IN")}`;
  const niceDate = (d) => {
    if (!d) return "—";
    try {
      const dt = new Date(d + "T00:00:00");
      return dt.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
    } catch { return d; }
  };
  const daysAway = (d) => {
    if (!d) return null;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const target = new Date(d + "T00:00:00");
    const diff = Math.round((target - today) / 86400000);
    if (diff === 0) return "today";
    if (diff === 1) return "tomorrow";
    return `in ${diff}d`;
  };

  const tiles = [
    {
      key: "leave",
      tone: "emerald",
      icon: Calendar,
      label: "Leave Balance",
      value: (Number.isInteger(total) ? total : total.toFixed(1)),
      unit: "days",
      sub: `Casual ${(bal.casual || 0)} · Sick ${(bal.sick || 0)} · Earned ${(bal.earned || 0)}`,
      href: "/m/hr_leaves",
      testid: "dash-tile-leave",
    },
    {
      key: "cash",
      tone: cashInHand > 0 ? "amber" : "slate",
      icon: IndianRupee,
      label: "Cash in Hand",
      value: fmt(cashInHand),
      unit: null,
      sub: cashInHand > 0 ? "Outstanding advance" : "All settled",
      href: "/m/accounts_claims",
      testid: "dash-tile-cash",
    },
    {
      key: "reimb",
      tone: reimbPending > 0 ? "indigo" : "slate",
      icon: Plane,
      label: "Reimb. Pending",
      value: fmt(reimbPending),
      unit: null,
      sub: reimbCount > 0 ? `${reimbCount} booking${reimbCount === 1 ? "" : "s"}` : "No pending",
      href: "/m/travel_booking",
      testid: "dash-tile-reimb",
    },
    {
      key: "holiday",
      tone: "violet",
      icon: PartyPopper,
      label: "Next Holiday",
      value: niceDate(nh?.date),
      unit: null,
      sub: nh ? `${nh.name} · ${daysAway(nh.date)}` : "No upcoming",
      href: "/m/holidays",
      testid: "dash-tile-holiday",
    },
  ];

  return (
    <div data-testid="leave-balance-card">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {tiles.map((t) => (
          <PulseTile key={t.key} {...t} />
        ))}
      </div>
    </div>
  );
}

const TILE_TONES = {
  emerald: { bg: "from-emerald-50/60 via-white to-teal-50/30",     border: "border-emerald-200 hover:border-emerald-300", iconBg: "from-emerald-500 to-teal-600",    ring: "ring-emerald-300/40", text: "text-emerald-700", label: "text-emerald-700", blob: "bg-emerald-200/40", shadow: "hover:shadow-emerald-100", iconColor: "text-emerald-600" },
  amber:   { bg: "from-amber-50/60 via-white to-orange-50/30",     border: "border-amber-200 hover:border-amber-300",     iconBg: "from-amber-500 to-orange-600",     ring: "ring-amber-300/40",   text: "text-amber-700",   label: "text-amber-700",   blob: "bg-amber-200/40",   shadow: "hover:shadow-amber-100",   iconColor: "text-amber-600" },
  indigo:  { bg: "from-indigo-50/60 via-white to-blue-50/30",      border: "border-indigo-200 hover:border-indigo-300",   iconBg: "from-indigo-500 to-blue-600",      ring: "ring-indigo-300/40",  text: "text-indigo-700",  label: "text-indigo-700",  blob: "bg-indigo-200/40",  shadow: "hover:shadow-indigo-100",  iconColor: "text-indigo-600" },
  violet:  { bg: "from-violet-50/60 via-white to-fuchsia-50/30",   border: "border-violet-200 hover:border-violet-300",   iconBg: "from-violet-500 to-fuchsia-600",   ring: "ring-violet-300/40",  text: "text-violet-700",  label: "text-violet-700",  blob: "bg-violet-200/40",  shadow: "hover:shadow-violet-100",  iconColor: "text-violet-600" },
  slate:   { bg: "from-slate-50/60 via-white to-slate-100/30",     border: "border-slate-200 hover:border-slate-300",     iconBg: "from-slate-400 to-slate-600",      ring: "ring-slate-300/40",   text: "text-slate-700",   label: "text-slate-600",   blob: "bg-slate-200/40",   shadow: "hover:shadow-slate-100",   iconColor: "text-slate-500" },
};

export function PulseTile({ tone, icon: Icon, label, value, unit, sub, href, testid }) {
  const t = TILE_TONES[tone] || TILE_TONES.emerald;
  const Inner = (
    <div
      className={`group pk-depth pk-depth-${tone || "emerald"} relative overflow-hidden rounded-xl p-4 h-full`}
      data-testid={testid}
    >
      {/* corner blob */}
      <span className={`absolute -top-8 -right-8 w-24 h-24 rounded-full ${t.blob} blur-2xl group-hover:opacity-90 transition-opacity`} aria-hidden="true" />

      <div className="relative flex flex-col gap-2 h-full">
        <div className="flex items-center gap-2">
          <Icon size={22} strokeWidth={2} className={`${t.iconColor} shrink-0 group-hover:scale-110 transition-transform`} />
          <div className={`text-[10px] uppercase tracking-[0.18em] font-extrabold ${t.label} truncate`}>{label}</div>
        </div>

        <div className="flex items-baseline gap-1.5 mt-1">
          <span className={`text-[26px] font-extrabold ${t.text} mono leading-none`}>{value}</span>
          {unit && <span className={`text-[11px] uppercase tracking-wider font-bold ${t.label} opacity-70`}>{unit}</span>}
        </div>

        <div className="text-[11px] text-slate-500 font-medium truncate">{sub}</div>
      </div>
    </div>
  );
  return href ? <Link to={href} className="block h-full">{Inner}</Link> : Inner;
}

export function TravelAdvanceCard() {
  const [summary, setSummary] = useState(null);
  const navigate = useNavigate();
  const load = () => api.get("/my-advance-summary").then(({ data }) => setSummary(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  // v109.9.179 — Live refresh when Accounts edits / disburses / ledgers.
  useDataChange(["travel:updated", "travel:ledgered", "travel:disbursed"], load);
  const taken = summary?.total_taken ?? 0;
  const settled = summary?.total_settled ?? 0;
  const out = summary?.outstanding ?? 0;
  const fmt = (n) => `₹${Number(n).toLocaleString("en-IN")}`;
  return (
    <div
      role="link"
      tabIndex={0}
      onClick={() => navigate("/m/accounts_advances")}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate("/m/accounts_advances"); } }}
      className="group pk-depth pk-depth-indigo relative overflow-hidden rounded-xl p-5 cursor-pointer h-full"
      data-testid="travel-advance-card"
    >
      <div className="absolute -top-8 -right-8 w-24 h-24 bg-indigo-200/40 rounded-full blur-2xl group-hover:opacity-80 transition-opacity" />
      <div className="relative">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <PlaneTakeoff size={22} strokeWidth={2} className="text-indigo-600 shrink-0 group-hover:scale-110 transition-transform" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-indigo-700">Travel Advance</div>
              <div className="text-[10.5px] text-indigo-600/80 font-semibold">{summary?.advances_count ?? 0} entries</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate("/m/accounts_advances"); }}
              className="text-[10.5px] text-indigo-700 hover:underline font-bold"
              data-testid="travel-advance-request-btn"
            >Request</button>
            <span className="text-[10.5px] text-slate-300">·</span>
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate("/m/travel?tab=settlement"); }}
              className="text-[10.5px] text-indigo-700 hover:underline font-bold"
              data-testid="travel-advance-settle-btn"
            >Settle</button>
          </div>
        </div>
        <div className="space-y-2">
          <Row k="Total Taken" v={fmt(taken)} testid="adv-taken" />
          <Row k="Settled" v={fmt(settled)} sub={`${summary?.settlements_count ?? 0} settlements`} testid="adv-settled" />
          <div className={`flex items-center justify-between border-t border-indigo-100 pt-2.5 ${out > 0 ? "text-amber-700" : "text-emerald-700"}`}>
            <div className="text-[10px] font-bold uppercase tracking-wider">Outstanding</div>
            <div className="heading text-xl font-extrabold mono" data-testid="adv-outstanding">{fmt(out)}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ k, v, sub, testid }) {
  return (
    <div className="flex items-center justify-between text-sm" data-testid={testid}>
      <div>
        <div className="text-slate-700">{k}</div>
        {sub && <div className="text-[10px] text-slate-400 mono">{sub}</div>}
      </div>
      <div className="mono font-semibold text-slate-900">{v}</div>
    </div>
  );
}

/**
 * ListSnapshotCard — a gradient-headed card that shows up to 4 list items below
 * its count summary. Used for: Active Tasks · Documents · Pending Calls · Interviews.
 * Whole tile is clickable (via useNavigate) — inner Links / buttons continue
 * to work with `stopPropagation`.
 */
function ListSnapshotCard({ tone, icon: Icon, label, count, total, href, items, renderItem, emptyText, testid }) {
  const t = TILE_TONES[tone] || TILE_TONES.emerald;
  const navigate = useNavigate();
  const onTileClick = href ? () => navigate(href) : undefined;
  const onTileKey = href ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate(href); } } : undefined;
  return (
    <div
      role={href ? "link" : undefined}
      tabIndex={href ? 0 : undefined}
      onClick={onTileClick}
      onKeyDown={onTileKey}
      className={`group pk-depth pk-depth-${tone || "emerald"} relative overflow-hidden rounded-xl p-4 flex flex-col h-full ${href ? "cursor-pointer" : ""}`}
      data-testid={testid}
    >
      <span className={`absolute -top-8 -right-8 w-24 h-24 rounded-full ${t.blob} blur-2xl group-hover:opacity-90 transition-opacity`} aria-hidden="true" />
      <div className="relative flex items-center gap-2 mb-3">
        <Icon size={22} strokeWidth={2} className={`${t.iconColor} shrink-0 group-hover:scale-110 transition-transform`} />
        <div className="flex-1 min-w-0">
          <div className={`text-[10px] uppercase tracking-[0.18em] font-extrabold ${t.label} truncate`}>{label}</div>
          <div className={`text-[12px] font-bold ${t.text}`}>{count} {count === 1 ? "item" : "items"}{typeof total === "number" && total > count ? ` · of ${total}` : ""}</div>
        </div>
        {href && (
          <span className={`text-[10px] ${t.label} group-hover:underline font-bold uppercase tracking-wider flex items-center gap-0.5 shrink-0`}>
            All <ArrowUpRight size={11} />
          </span>
        )}
      </div>
      <div className="relative space-y-1.5 flex-1">
        {items.length === 0 && <div className="text-[12px] text-slate-400 italic">{emptyText}</div>}
        {items.slice(0, 4).map((it, i) => (
          <div key={it.id || i} className="bg-white/70 backdrop-blur-sm border border-white/40 rounded-md px-2.5 py-1.5 hover:bg-white hover:border-slate-200 transition-colors">
            {renderItem(it)}
          </div>
        ))}
      </div>
    </div>
  );
}

export function WorkspaceSnapshotRow() {
  const [adv, setAdv] = useState({ count: 0, total: 0, outstanding: 0 });
  const [tasks, setTasks] = useState([]);
  const [docs, setDocs] = useState([]);

  const load = () => Promise.all([
    api.get("/my-advance-summary").catch(() => ({ data: null })),
    api.get("/tasks?scope=mine").catch(() => ({ data: [] })),
    api.get("/documents").catch(() => ({ data: [] })),
  ]).then(([a, t, d]) => {
    setAdv({ count: a.data?.advances_count ?? 0, outstanding: a.data?.outstanding ?? 0 });
    const allTasks = Array.isArray(t.data) ? t.data : [];
    const active = allTasks.filter((x) => String(x.status || "").toLowerCase() !== "completed");
    setTasks(active);
    setDocs(Array.isArray(d.data) ? d.data : []);
  });
  useEffect(() => { load(); }, []);
  // v109.9.179 — Live refresh.
  useDataChange(["travel:updated", "travel:ledgered", "travel:disbursed"], load);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="snapshot-workspace">
      {/* Travel Advance — outstanding pulse tile.
          v109.9.175 — Shows the current unsettled balance for the user:
          count of Disbursed advances not yet balanced by a Ledgered
          settlement, plus the ₹ outstanding total. */}
      <PulseTile
        tone={adv.count > 0 ? "amber" : "slate"}
        icon={PlaneTakeoff}
        label="Travel Advance"
        value={adv.count}
        unit={adv.count === 1 ? "unsettled" : "unsettled"}
        sub={adv.count > 0 ? `₹${Number(adv.outstanding || 0).toLocaleString("en-IN")} outstanding` : "All settled"}
        href="/m/accounts_advances"
        testid="dash-tile-travel-advance"
      />

      {/* Active Tasks — list of assigned + collaborator + reopened items */}
      <ListSnapshotCard
        tone={tasks.length > 0 ? "violet" : "slate"}
        icon={ClipboardList}
        label="Active Tasks"
        count={tasks.length}
        href="/tasks"
        items={tasks}
        emptyText="No active tasks"
        testid="dash-list-active-tasks"
        renderItem={(t) => (
          <Link to="/tasks" className="block">
            <div className="flex items-center gap-2">
              <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                String(t.status || "").toLowerCase().includes("reopen") ? "bg-rose-500"
                : String(t.status || "").toLowerCase().includes("progress") ? "bg-amber-500"
                : String(t.status || "").toLowerCase().includes("review") ? "bg-indigo-500"
                : "bg-violet-500"
              }`} />
              <span className="text-[12px] font-semibold text-slate-800 truncate flex-1">{t.title}</span>
              {t.deadline && <span className="text-[10px] text-slate-500 mono shrink-0">{t.deadline}</span>}
            </div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">{t.status || "Open"}</div>
          </Link>
        )}
      />

      {/* Documents Corner — list of recent docs from /documents */}
      <ListSnapshotCard
        tone={docs.length > 0 ? "indigo" : "slate"}
        icon={FolderArchive}
        label="Documents Corner"
        count={docs.length}
        href="/m/documents"
        items={docs}
        emptyText="No documents shared"
        testid="dash-list-documents"
        renderItem={(d) => (
          <Link to="/m/documents" className="block">
            <div className="flex items-center gap-2">
              <FolderArchive size={11} className="text-indigo-500 shrink-0" />
              <span className="text-[12px] font-semibold text-slate-800 truncate flex-1">{d.title || d.name || "Untitled"}</span>
            </div>
            {(d.category || d.uploaded_at) && (
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5 truncate">
                {d.category || ""}
                {d.category && d.uploaded_at ? " · " : ""}
                {d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : ""}
              </div>
            )}
          </Link>
        )}
      />
    </div>
  );
}

export function ApprovalsSnapshotRow() {
  const [disb, setDisb] = useState([]);
  const [calls, setCalls] = useState([]);
  const [interviews, setInterviews] = useState([]);
  // Respond-to-Pending-Call dialog state
  const [respondTarget, setRespondTarget] = useState(null);
  const [called, setCalled] = useState(null);
  const [remark, setRemark] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    Promise.all([
      api.get("/travel-advances/pending-disbursement").catch(() => ({ data: [] })),
      api.get("/lead-call-requests?scope=mine").catch(() => ({ data: [] })),
      api.get("/my-upcoming-interviews").catch(() => ({ data: [] })),
    ]).then(([d, c, i]) => {
      setDisb(Array.isArray(d.data) ? d.data : []);
      setCalls((Array.isArray(c.data) ? c.data : []).filter((x) => String(x.status || "").toLowerCase() === "pending"));
      setInterviews(Array.isArray(i.data) ? i.data : []);
    });
  };

  useEffect(() => {
    load();
    // Refresh when window regains focus so that submitting a CRM report
    // elsewhere clears this widget within seconds.
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    const t = setInterval(load, 30000);
    return () => { window.removeEventListener("focus", onFocus); clearInterval(t); };
  }, []);
  // v109.9.179 — Live refresh on any Travel record mutation.
  useDataChange(["travel:updated", "travel:disbursed"], load);

  const submitRespond = async () => {
    if (!respondTarget) return;
    if (called === null) { toast.error("Please choose Yes or No first"); return; }
    if (called === true && !remark.trim()) { toast.error("Please add a remark about the call"); return; }
    setBusy(true);
    try {
      await api.patch(`/lead-call-requests/${respondTarget.id}`, {
        called,
        log_remark: called ? remark.trim() : "Not called yet — will follow up within 3 days",
      });
      toast.success(called ? "Call logged in CRM Reporting" : "Marked as not yet called — admin notified");
      setRespondTarget(null);
      setCalled(null);
      setRemark("");
      load();
    } catch (e) {
      toast.error(formatError(e) || "Could not submit response");
    } finally {
      setBusy(false);
    }
  };

  const disbTotal = disb.reduce((acc, x) => acc + Number(x.advance_amount || 0), 0);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="snapshot-approvals">
      {/* Pending Disbursement — amount-based pulse tile */}
      <PulseTile
        tone={disb.length > 0 ? "amber" : "slate"}
        icon={Banknote}
        label="Pending Disbursement"
        value={disb.length}
        unit={disb.length === 1 ? "advance" : "advances"}
        sub={disb.length > 0 ? `₹${disbTotal.toLocaleString("en-IN")} pending` : "All clear"}
        href="/m/accounts_advances"
        testid="dash-tile-pending-disb"
      />

      {/* Pending Call Requests — list with status badge + Respond button per item */}
      <ListSnapshotCard
        tone={calls.length > 0 ? "rose" : "slate"}
        icon={PhoneCall}
        label="Pending Call Requests"
        count={calls.length}
        href="/m/sales_crm_report"
        items={calls}
        emptyText="No pending requests"
        testid="dash-list-call-requests"
        renderItem={(c) => {
          // Compute due chip — overdue (>3d), due-in-N, or just pending
          let dueChip = null;
          if (c.requested_at) {
            try {
              const reqDate = new Date(c.requested_at);
              const ageDays = Math.floor((Date.now() - reqDate.getTime()) / 86400000);
              if (ageDays > 3) dueChip = { text: "Overdue", cls: "bg-rose-600 text-white border-rose-700" };
              else if (ageDays >= 2) dueChip = { text: "Due now", cls: "bg-amber-500 text-white border-amber-600" };
              else dueChip = { text: `Due in ${3 - ageDays}d`, cls: "bg-amber-100 text-amber-800 border-amber-200" };
            } catch { /* ignore */ }
          }
          return (
            <div className="flex items-center gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <PhoneCall size={11} className="text-rose-500 shrink-0" />
                  <span className="text-[12px] font-semibold text-slate-800 truncate">{c.client_name || c.school_name || "Lead"}</span>
                </div>
                <div className="flex items-center gap-1.5 mt-0.5">
                  {dueChip && (
                    <span className={`text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded-full border ${dueChip.cls}`}>{dueChip.text}</span>
                  )}
                  <span className="text-[10px] text-slate-500 truncate">{c.requested_by_name ? `by ${c.requested_by_name}` : ""}</span>
                </div>
              </div>
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setRespondTarget(c); setCalled(null); setRemark(""); }}
                className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-md bg-rose-600 text-white hover:bg-rose-700 active:scale-95 transition-all shrink-0 shadow-sm shadow-rose-200/50"
                data-testid={`dash-call-respond-${c.id}`}
              >
                Respond
              </button>
            </div>
          );
        }}
      />

      {/* Upcoming Interviews — list with candidate + schedule */}
      <ListSnapshotCard
        tone={interviews.length > 0 ? "violet" : "slate"}
        icon={UsersRound}
        label="Upcoming Interviews"
        count={interviews.length}
        href="/m/hr_interview"
        items={interviews}
        emptyText="None scheduled"
        testid="dash-list-interviews"
        renderItem={(iv) => {
          const dt = iv.scheduled_at ? new Date(iv.scheduled_at) : null;
          const candidate = iv.interviewee?.name || iv.candidate_name || iv.name || "Candidate";
          return (
            <Link to="/m/hr_interview" className="block">
              <div className="flex items-center gap-2">
                <UsersRound size={11} className="text-violet-500 shrink-0" />
                <span className="text-[12px] font-semibold text-slate-800 truncate flex-1">{candidate}</span>
                {iv.round && <span className="text-[9px] uppercase font-bold text-violet-600 bg-violet-100 px-1.5 py-0.5 rounded-full shrink-0">{iv.round.replace("Round", "").trim()}</span>}
              </div>
              {dt && (
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5 mono">
                  {dt.toLocaleDateString("en-IN", { day: "numeric", month: "short" })} · {dt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                </div>
              )}
            </Link>
          );
        }}
      />

      {/* Respond-to-Pending-Call dialog (single instance, controlled by `respondTarget`).
          Did-you-call → Yes (remark required → logged in CRM Reporting) /
          No (3-day warning, escalates to admin if not closed) */}
      <Dialog open={!!respondTarget} onOpenChange={(o) => { if (!o) { setRespondTarget(null); setCalled(null); setRemark(""); } }}>
        <DialogContent className="max-w-md" data-testid="dash-call-respond-dialog">
          <DialogHeader>
            <DialogTitle className="heading text-[18px]">
              Did you call <span className="text-rose-600">{respondTarget?.client_name || respondTarget?.school_name || "this client"}</span>?
            </DialogTitle>
            <DialogDescription className="text-[12px] text-slate-500">
              {respondTarget?.requested_by_name ? `Requested by ${respondTarget.requested_by_name}` : "Lead call request"}
              {respondTarget?.requested_at && ` · ${new Date(respondTarget.requested_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="grid grid-cols-2 gap-2">
              <Button
                onClick={() => setCalled(true)}
                className={`h-10 ${called === true ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200"}`}
                data-testid="dash-call-yes"
              >
                <PhoneIncoming size={14} className="mr-1.5" /> Yes — call made
              </Button>
              <Button
                onClick={() => setCalled(false)}
                className={`h-10 ${called === false ? "bg-rose-600 hover:bg-rose-700 text-white" : "bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200"}`}
                data-testid="dash-call-no"
              >
                <PhoneOff size={14} className="mr-1.5" /> Not yet
              </Button>
            </div>
            {called === true && (
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-wider text-slate-600">Log Remark *</Label>
                <Textarea
                  value={remark}
                  onChange={(e) => setRemark(e.target.value)}
                  placeholder="Outcome of the call, next steps, key takeaways…"
                  rows={4}
                  data-testid="dash-call-remark"
                />
                <p className="text-[10.5px] text-slate-500">This remark will be saved as a CRM Report against this client and shared with the original requester.</p>
              </div>
            )}
            {called === false && (
              <div className="text-[12px] text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-3 leading-relaxed">
                ⏳ Please call this school within 3 days. If you don't, this will surface on your admin's dashboard.
              </div>
            )}
          </div>
          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => { setRespondTarget(null); setCalled(null); setRemark(""); }} disabled={busy}>
              Cancel
            </Button>
            <Button
              onClick={submitRespond}
              disabled={busy || called === null}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="dash-call-submit"
            >
              {busy ? "Saving…" : "Save Response"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Tone palette extension — rose for tasks, blue + orange to match Dashboard StatCard tones.
TILE_TONES.rose = { bg: "from-rose-50/60 via-white to-pink-50/30", border: "border-rose-200 hover:border-rose-300", iconBg: "from-rose-500 to-pink-600", ring: "ring-rose-300/40", text: "text-rose-700", label: "text-rose-700", blob: "bg-rose-200/40", shadow: "hover:shadow-rose-100", iconColor: "text-rose-600" };
TILE_TONES.blue = { bg: "from-blue-50/60 via-white to-indigo-50/30", border: "border-blue-200 hover:border-blue-300", iconBg: "from-blue-500 to-indigo-600", ring: "ring-blue-300/40", text: "text-blue-700", label: "text-blue-700", blob: "bg-blue-200/40", shadow: "hover:shadow-blue-100", iconColor: "text-blue-600" };
TILE_TONES.orange = { bg: "from-orange-50/60 via-white to-red-50/30", border: "border-orange-200 hover:border-orange-300", iconBg: "from-orange-500 to-red-600", ring: "ring-orange-300/40", text: "text-orange-700", label: "text-orange-700", blob: "bg-orange-200/40", shadow: "hover:shadow-orange-100", iconColor: "text-orange-600" };
TILE_TONES.sky = { bg: "from-sky-50/60 via-white to-cyan-50/30", border: "border-sky-200 hover:border-sky-300", iconBg: "from-sky-500 to-cyan-600", ring: "ring-sky-300/40", text: "text-sky-700", label: "text-sky-700", blob: "bg-sky-200/40", shadow: "hover:shadow-sky-100", iconColor: "text-sky-600" };

